// /admin/ops/wallets/$walletId/flows — wallet flow chain visualizer
// for balance findings. The breaking row is highlighted in red.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { fetchWalletFlows, opsKeys } from "@/lib/ops-queries";

interface Search {
  field?: string;
  break_flow_id?: number;
}

export const Route = createFileRoute("/_app/ops/wallets/$walletId/flows")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    field: typeof s.field === "string" ? s.field : "AVAILABLE",
    break_flow_id: typeof s.break_flow_id === "number" ? s.break_flow_id : Number(s.break_flow_id ?? 0),
  }),
  component: WalletFlows,
});

function WalletFlows() {
  const { walletId } = Route.useParams();
  const search = Route.useSearch();
  const field = search.field ?? "AVAILABLE";
  const breakFlowID = search.break_flow_id ?? 0;

  const q = useQuery({
    queryKey: opsKeys.walletFlows(walletId, field, breakFlowID),
    queryFn: () => fetchWalletFlows(walletId, field, breakFlowID),
  });

  return (
    <div className="space-y-4">
      <Link to="/ops/findings" className="text-sm text-muted hover:text-fg">← Back to inbox</Link>

      <Card>
        <CardHeader>
          <CardTitle>
            Wallet flow chain — <code className="font-mono text-base">{walletId}</code> / {field}
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading && <Skeleton className="m-5 h-32" />}
          {q.data && (
            <table className="w-full text-xs font-mono">
              <thead className="text-2xs uppercase tracking-wide text-muted">
                <tr className="border-b border-border">
                  <th className="px-2 py-2 text-left">flow_id</th>
                  <th className="px-2 py-2 text-left">kind</th>
                  <th className="px-2 py-2 text-right">delta</th>
                  <th className="px-2 py-2 text-right">expected_after</th>
                  <th className="px-2 py-2 text-right">actual_after</th>
                  <th className="px-2 py-2 text-right">diff</th>
                  <th className="px-2 py-2 text-left">txn</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((row) => (
                  <tr
                    key={row.FlowID}
                    className={row.IsBreak ? "bg-red-500/10 border-b border-red-500/40" : "border-b border-border last:border-0"}
                  >
                    <td className="px-2 py-1">
                      {row.IsBreak && <Badge color="red">break</Badge>} {row.FlowID}
                    </td>
                    <td className="px-2 py-1">{row.FlowKind}</td>
                    <td className="px-2 py-1 text-right">{formatSigned(row.AmountDelta)}</td>
                    <td className="px-2 py-1 text-right">{row.ExpectedBalance.toLocaleString()}</td>
                    <td className={`px-2 py-1 text-right ${row.IsBreak ? "text-red-500 font-bold" : ""}`}>
                      {row.BalanceAfter.toLocaleString()}
                    </td>
                    <td className={`px-2 py-1 text-right ${row.IsBreak ? "text-red-500" : "text-muted"}`}>
                      {row.IsBreak ? formatSigned(row.BalanceAfter - row.ExpectedBalance) : "—"}
                    </td>
                    <td className="px-2 py-1 truncate max-w-[20ch]" title={row.TransactionID}>{row.TransactionID}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function formatSigned(n: number): string {
  if (n > 0) return `+${n.toLocaleString()}`;
  return n.toLocaleString();
}
